import { useApiClient } from "./api/client";
import { useBaseStore } from "./stores/base";
import { Result, ApiError } from "./api/base";
import UserDo = App.Data.UserDO;
import { registerSpecificProductPlanTranslations } from "@/ui/product-plans";
import { setLocale } from "chartjs-graph";
import { initEcho, tenantChannel } from "@/plugins/echo";
import { handleExportProgressEvent } from "@/stores/exportStore";
import { handlePriceListImportProgressEvent } from "@/stores/priceListImportStore";

/**
 * Checks if the user is authenticated and sets the base store accordingly.
 * If not authenticated, redirect to the login page.
 *
 * @returns The current user or an error.
 */
export async function appInit(): Promise<Result<UserDo, ApiError>> {
	const baseStore = useBaseStore();
	const apiClientUsers = useApiClient("users");
	const apiClientNavigation = useApiClient("navigation");
	const apiClientEnvironment = useApiClient("environment");

	const currentUserResponse = await apiClientUsers.getCurrentUser();
	if (currentUserResponse.isErr()) {
		return currentUserResponse;
	}

	baseStore.currentUser = currentUserResponse.value;
	baseStore.isAuthenticated = true;

	const [permissionsResponse, navigationResponse, environmentResponse] =
		await Promise.all([
			apiClientUsers.getCurrentUserPermissions(),
			apiClientNavigation.getNavigation(),
			apiClientEnvironment.getEnvironment(),
		]);

	if (permissionsResponse.isOk()) {
		baseStore.permissions = permissionsResponse.value;
	}

	if (navigationResponse.isOk()) {
		baseStore.SET_NAVIGATION_VISIBILITY(navigationResponse.value);
	}

	if (environmentResponse.isOk()) {
		baseStore.currentEnvironment = environmentResponse.value;

		// overwrite translations that are specific for a product-plan
		if (environmentResponse.value?.product_plan?.name_clean) {
			registerSpecificProductPlanTranslations(
				environmentResponse.value.product_plan.name_clean
			);
		}
		setLocale(baseStore.currentEnvironment.number_format_locale);

		initEcho(baseStore.currentEnvironment.reverb);

		// Subscribe once per session to the user's private channel for export and
		// price list import progress. The environment is loaded here, so
		// tenantChannel() has the broadcast prefix.
		if (baseStore.currentUser) {
			window.Echo.private(
				tenantChannel(`App.Models.User.${baseStore.currentUser.id}`)
			)
				.listen(".ExportProgressUpdated", handleExportProgressEvent)
				.listen(".ExportProgressDownloaded", handleExportProgressEvent)
				.listen(
					".PriceListImportProgressUpdated",
					handlePriceListImportProgressEvent
				);
		}
	}

	return currentUserResponse;
}
